import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Pencil, Plus, RefreshCw, Sparkles, Trash2 } from 'lucide-react'
import {
  useCleaningItemsAdmin,
  useCreateCleaningItem,
  useUpdateCleaningItem,
  useDeleteCleaningItem,
} from '@/hooks/data/useCatalogAdmin'
import type { CatalogItemAdmin, CatalogItemInput } from '@/types/domain'
import EditModal from '@/components/crud/EditModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'

function ItemsPage() {
  const { data: items = [], isLoading, isError, error, isFetching, refetch } = useCleaningItemsAdmin()
  const create = useCreateCleaningItem()
  const update = useUpdateCleaningItem()
  const del = useDeleteCleaningItem()
  const confirmDelete = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<CatalogItemAdmin | null>(null)

  const rows = items.filter((i) => !q || i.name.toLowerCase().includes(q.toLowerCase()))

  const fields = [
    { key: 'code', label: 'Code', required: true },
    { key: 'name', label: 'Name', required: true },
    { key: 'price', label: 'Price', type: 'number' as const, required: true },
    { key: 'durationMinutes', label: 'Duration (minutes)', type: 'number' as const, required: true },
    {
      key: 'active',
      label: 'Active',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ]

  const toInput = (values: Record<string, any>): CatalogItemInput => ({
    code: values.code,
    name: values.name,
    price: Number(values.price),
    durationMinutes: Number(values.durationMinutes),
    active: values.active === 'true',
  })

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6" /> Cleaning Items
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Add-on cleaning items and their pricing (CatalogAdminController).</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Item
          </button>
        </div>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search items…"
        className="w-full md:w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
      />

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading items…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load items: {(error as Error)?.message}.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-gray-500">No items found.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Price</th>
                <th className="px-4 py-2">Duration</th>
                <th className="px-4 py-2">Active</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{i.name}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{i.code}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">${i.price.toFixed(2)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{i.durationMinutes} min</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        i.active
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {i.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(i)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit item">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          confirmDelete(i.name, async () => {
                            await del.mutateAsync(i.id)
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Cleaning Item"
        fields={fields}
        initialValues={{ active: 'true' }}
        submitLabel="Create"
        onSubmit={async (values) => {
          await create.mutateAsync(toInput(values))
        }}
      />

      <EditModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Cleaning Item"
        description={editing?.name}
        fields={fields}
        initialValues={
          editing
            ? {
                code: editing.code,
                name: editing.name,
                price: editing.price,
                durationMinutes: editing.durationMinutes,
                active: editing.active ? 'true' : 'false',
              }
            : {}
        }
        onSubmit={async (values) => {
          if (!editing) return
          await update.mutateAsync({ id: editing.id, body: toInput(values) })
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/items/')({
  component: ItemsPage,
})
